import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";

import { ThemeToggle } from "@/components/ThemeToggle";
import { toneClass, type FoldTone } from "@/components/Section";
import { useConfig } from "@/config/ConfigProvider";
import { assetUrl } from "@/config/schema";
import { cn } from "@/lib/utils";

/** Watches which fold sits under the header and reports its tone. */
function useActiveFoldTone() {
  const [tone, setTone] = useState<FoldTone | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const HEADER = 72;

    const readTone = () => {
      setScrolled(window.scrollY > 12);
      const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-fold-tone]"));
      let current: FoldTone | null = null;
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= HEADER && rect.bottom > HEADER) {
          current = Number(section.dataset["foldTone"]) as FoldTone;
        }
      }
      setTone(current);
    };

    readTone();
    window.addEventListener("scroll", readTone, { passive: true });
    window.addEventListener("resize", readTone);
    return () => {
      window.removeEventListener("scroll", readTone);
      window.removeEventListener("resize", readTone);
    };
  }, []);

  return { tone, scrolled };
}

export function Header() {
  const config = useConfig();
  const posthog = usePostHog();
  const { nav, brand, featureFlags } = config;
  const { tone, scrolled } = useActiveFoldTone();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const logo = assetUrl(config, brand.logoImage);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,color] duration-300",
        scrolled && tone !== null
          ? cn(
              toneClass[tone],
              "shadow-[0_1px_0_0_color-mix(in_srgb,currentColor_12%,transparent)]",
            )
          : "bg-transparent text-[var(--on-dark-fold)]",
      )}
    >
      <div className="mx-auto flex h-[72px] max-w-6xl items-center gap-4 px-5 sm:px-8">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 font-heading text-lg font-bold tracking-tight"
        >
          {logo ? (
            <img src={logo} alt="" width={32} height={32} className="h-8 w-8 shrink-0" />
          ) : (
            <span
              aria-hidden
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--accent)] text-[var(--accent-foreground)] text-sm font-black"
            >
              {brand.name.charAt(0)}
            </span>
          )}
          <span className="truncate">{brand.logoText || brand.name}</span>
        </Link>

        <nav className="ml-auto hidden items-center gap-1 lg:flex">
          {nav.links.map((link) => (
            <NavLink
              key={link.to + link.label}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors hover:bg-current/10 ${
                  isActive ? "bg-current/10" : ""
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          {featureFlags.themeToggle ? <ThemeToggle className="hidden sm:inline-flex" /> : null}
          <a
            href={`tel:${brand.phone.replace(/\s+/g, "")}`}
            onClick={() => posthog?.capture("phone_number_clicked", { location: "header" })}
            className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold hover:bg-current/10 md:inline-flex"
          >
            <Phone className="h-4 w-4" aria-hidden />
            {brand.phone}
          </a>
          <Link
            to={nav.cta.to}
            onClick={() => posthog?.capture("nav_cta_clicked", { destination: nav.cta.to })}
            className="hidden rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent-foreground)] shadow-sm transition-transform duration-200 hover:brightness-110 active:scale-[0.97] sm:inline-flex"
          >
            {nav.cta.label}
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-current/10 lg:hidden"
          >
            {open ? <Menu className="h-5 w-5 rotate-90" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open ? (
        <div className="fixed inset-0 top-0 z-50 bg-[var(--surface-4)] text-[var(--on-dark-fold)] lg:hidden">
          <div className="flex h-[72px] items-center justify-between px-5">
            <span className="font-heading text-lg font-bold">{brand.logoText || brand.name}</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="grid h-11 w-11 place-items-center rounded-full hover:bg-current/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-col gap-1 px-5 pt-4">
            {nav.links.map((link) => (
              <Link
                key={link.to + link.label}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-4 font-heading text-xl font-semibold hover:bg-current/10"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to={nav.cta.to}
              onClick={() => { setOpen(false); posthog?.capture("nav_cta_clicked", { destination: nav.cta.to }); }}
              className="mt-4 rounded-xl bg-[var(--accent)] px-4 py-4 text-center font-semibold text-[var(--accent-foreground)]"
            >
              {nav.cta.label}
            </Link>
            <a
              href={`tel:${brand.phone.replace(/\s+/g, "")}`}
              onClick={() => posthog?.capture("phone_number_clicked", { location: "header_mobile" })}
              className="mt-2 rounded-xl px-4 py-4 text-center font-semibold hover:bg-current/10"
            >
              {brand.phone}
            </a>
            {featureFlags.themeToggle ? (
              <div className="mt-6 flex justify-center">
                <ThemeToggle />
              </div>
            ) : null}
          </nav>
        </div>
      ) : null}
    </header>
  );
}
